import React, { forwardRef } from 'react';

import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { AlineButton, AlineDialog, BaseSnackbar, RouterLink } from 'src/components';
import type { AlineDialogProps } from 'src/components/generic';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { Apollo } from 'src/gql/generated';
import { fakeNotistackMessage } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  saveButton: {
    marginLeft: theme.spacing(1),
  },
  text: {
    marginBottom: theme.spacing(3),
  },
}));

interface TrashSnackProps {
  docName: string;
  id: string;
}

const TrashSnack = forwardRef<HTMLDivElement, TrashSnackProps>(function TrashSnack(
  props: TrashSnackProps,
  ref
): JSX.Element {
  const { docName, id } = props;

  return (
    <BaseSnackbar ref={ref} id={id} variant="success">
      <Typography variant="caption">{`${docName} successfully restored!`}</Typography>
    </BaseSnackbar>
  );
});

type TrashDialogProps = Omit<AlineDialogProps, 'title' | 'onClose' | 'open'>;

export function TrashDialog(props: TrashDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const {
    documentUi: { setTrashDialogOpen },
  } = useEditorTabStateActions();
  const { enqueueSnackbar } = useSnackbar();
  const { document } = useGetCurrentDoc();
  const [restoreStorables] = Apollo.useRestoreStorablesMutation();

  const trashDialogOpen = useEditorTabStateSelector(state => state.documentUi.trashDialogOpen);

  function closeDialog(): void {
    setTrashDialogOpen(false);
  }

  async function restoreDoc(): Promise<void> {
    if (!document) return;

    await restoreStorables({ variables: { restoreProps: { storableIds: [document.id] } } });

    closeDialog();

    const key = uuid4();
    enqueueSnackbar(fakeNotistackMessage, {
      content: <TrashSnack docName={document.name} id={key} />,
      key,
    });
  }

  return (
    <AlineDialog
      disableAltClose
      open={trashDialogOpen}
      title="File has been moved to trash"
      onClose={closeDialog}
      {...props}
    >
      <Typography className={classes.text} variant="body1">
        To access this file, take it out of the trash.
      </Typography>
      <footer className={classes.footer}>
        <AlineButton
          className={classes.text}
          color="primary"
          variant="outlined"
          onClick={restoreDoc}
        >
          Take out of trash
        </AlineButton>
        <AlineButton
          className={classes.saveButton}
          color="primary"
          component={RouterLink}
          to="/"
          variant="contained"
          onClick={closeDialog}
        >
          Home
        </AlineButton>
      </footer>
    </AlineDialog>
  );
}
